/**
 * Share-link encoding: the whole preset JSON, base64url-encoded in the URL
 * fragment. The fragment never reaches any server — this is the zero-backend
 * preset transfer path (permanent; part of the privacy story).
 */

import type { Preset } from "../../schema/types.ts";
import { validatePreset } from "../../schema/validator.ts";

const PARAM = "#p=";

export function encodeShareLink(preset: Preset): string {
  const json = JSON.stringify(preset);
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  const b64 = btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return location.origin + location.pathname + PARAM + b64;
}

/** Returns the preset from location.hash, or null (invalid links throw). */
export function decodeShareLink(hash: string): Preset | null {
  if (!hash.startsWith(PARAM)) return null;
  const b64 = hash.slice(PARAM.length).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const doc: unknown = JSON.parse(new TextDecoder().decode(bytes));
  const result = validatePreset(doc);
  if (!result.ok) {
    throw new Error(`share link contains an invalid preset:\n${result.errors.join("\n")}`);
  }
  return result.preset;
}
